<template>
  <div
    v-if="loaded"
    class="bg-white rounded-xl relative flex flex-wrap lg:flex-nowrap lg:w-full shadow transition-all duration-300 ease-in-out"
  >
    <div class="relative px-8 py-4 w-full">
      <!-- Post content -->
      <div class="border-b border-truegray-300 pb-4 z-40">
        <!-- CATEGORY MOBILE -->
        <div class="block lg:hidden w-1/4 mb-4">
          <h6
            :class="[
              post.category === 1 ? 'text-white bg-truegray-500' : '',
              post.category === 2 ? 'bg-truegray-100 text-black' : '',
              post.category === 3 ? 'bg-black text-white' : '',
            ]"
            class="inline p-2 px-4 rounded"
          >
            {{ post.category_title }}
          </h6>
        </div>
        <h3>{{ post.title }}</h3>
        <h6 v-if="region_tag" class="text-truegray-500">
          Region:
          <span class="text-cw-alt font-semibold ml-2">{{
            regionTagText
          }}</span>
        </h6>
        <h6 v-if="post.category === 3" class="text-truegray-500">
          <span v-if="post.event_start_at_time"
            >{{ post.event_start_at_time }} |
          </span>
          <span>{{ post.event_start_at_ui }} </span>
          <span v-if="post.event_start_at_ui != post.event_end_at_ui">
            - {{ post.event_end_at_ui }}
          </span>
        </h6>
        <h6 v-if="post.category === 2" class="text-truegray-500">
          <span v-if="post.job_employer">{{ post.job_employer }} | </span>
          {{ jobTypes[post.job_type].text }}
        </h6>
        <h6 v-if="post.category !== 3" class="text-truegray-500">
          Published: {{ post.created_at_ui }}
        </h6>
      </div>

      <div class="pt-1">
        <!-- POST INFO -->
        <div v-if="post.statistics" class="mb-4 text-truegray-500">
          <h6 v-if="post.statistics.shares > 1">
            {{ post.statistics.shares }} schools have shared this post
          </h6>
          <h6 v-if="post.statistics.shares === 1">
            1 school has shared this post
          </h6>
          <h6 v-if="post.statistics.shares === 0">
            No schools have shared this post yet
          </h6>
        </div>

        <AuthorBadge :entity="post.author" :published-at="post.created_at_ui" />
      </div>
      <!-- Saved overlay -->
      <div
        v-if="saved"
        class="saved-modal rounded-l-xl top-0 left-0 z-50 w-full absolute h-full flex flex-col items-center justify-center space-y-3"
      >
        <h3>Post already saved</h3>
        <p>Click here to view your Cloud posts</p>
        <p class="italic text-truegray-500 text-center px-6">
          If you used save and publish, the post will be live on your settings.
        </p>
        <button class="btn btn-trans-black" @click="goToCloudPosts">
          View Cloud Drafts
        </button>
      </div>
    </div>
    <!-- Buttons -->
    <div
      class="flex flex-wrap lg:flex-col lg:border-l border-truegray-300 w-full lg:w-48 overflow-hidden rounded-r-xl"
    >
      <div
        class="flex flex-col items-center justify-center space-y-2 text-truegray-500 h-16 lg:h-1/3 w-1/2 lg:w-full cursor-pointer hover:bg-truegray-100 transition-all duration-100"
        @click="viewPost"
      >
        <a
          ref="viewPost"
          :href="settings.cloud_domain + '/cloudpost/' + post.id"
          target="_blank"
          class="hidden"
        ></a>
        <h6>Preview</h6>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-5 w-5"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
          <path
            fill-rule="evenodd"
            d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
            clip-rule="evenodd"
          />
        </svg>
      </div>

      <button
        class="flex flex-col items-center justify-center space-y-2 h-16 lg:h-1/3 w-1/2 lg:w-full cursor-pointer transition-all duration-100"
        :class="[
          saved || thinking || tenantId === post.tenant_id
            ? 'pointer-events-none'
            : '',
          draftThinking ? 'bg-cw-900' : 'bg-truegray-100 hover:bg-tuegray-300',
        ]"
        :disabled="tenantId === post.tenant_id || thinking"
        @click="saveCloudPost(post.id, post.tenant_id)"
      >
        <lottie v-if="draftThinking" :options="loadingAnimationOptions" />
        <h6 v-if="!saved && !thinking && !draftThinking">Save as draft</h6>
        <svg
          v-if="!draftThinking"
          xmlns="http://www.w3.org/2000/svg"
          class="h-5 w-5"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            d="M7.707 10.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V6h5a2 2 0 012 2v7a2 2 0 01-2 2H4a2 2 0 01-2-2V8a2 2 0 012-2h5v5.586l-1.293-1.293zM9 4a1 1 0 012 0v2H9V4z"
          />
        </svg>
      </button>

      <button
        class="flex flex-col items-center justify-center space-y-2 text-white h-16 lg:h-1/3 w-1/2 lg:w-full hover:bg-cw-900 cursor-pointer transition-all duration-100"
        :class="[
          saved || thinking || tenantId === post.tenant_id
            ? 'bg-cw-900'
            : 'bg-cw-300',
        ]"
        :disabled="tenantId === post.tenant_id || thinking"
        @click="saveCloudPost(post.id, post.tenant_id, true)"
      >
        <lottie v-if="thinking" :options="loadingAnimationOptions" />
        <h6>
          <span v-if="tenantId === post.tenant_id && !thinking"
            >Posted by you</span
          ><span v-if="!saved && !thinking && tenantId !== post.tenant_id"
            >Save and publish</span
          ><span v-if="saved && !thinking && tenantId !== post.tenant_id"
            >Saved</span
          >
        </h6>
        <svg
          v-if="!saved && !thinking && tenantId !== post.tenant_id"
          xmlns="http://www.w3.org/2000/svg"
          class="h-5 w-5"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            d="M7.707 10.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V6h5a2 2 0 012 2v7a2 2 0 01-2 2H4a2 2 0 01-2-2V8a2 2 0 012-2h5v5.586l-1.293-1.293zM9 4a1 1 0 012 0v2H9V4z"
          />
        </svg>
        <svg
          v-if="
            (saved ||
              (tenantId === post.tenant_id && post.author.type === 1)) &&
            !thinking
          "
          xmlns="http://www.w3.org/2000/svg"
          class="h-5 w-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            d="M3.105 2.289a.75.75 0 00-.826.95l1.414 4.925A1.5 1.5 0 005.135 9.25h6.115a.75.75 0 010 1.5H5.135a1.5 1.5 0 00-1.442 1.086l-1.414 4.926a.75.75 0 00.826.95 28.896 28.896 0 0015.293-7.154.75.75 0 000-1.115A28.897 28.897 0 003.105 2.289z"
          />
        </svg>
      </button>
    </div>
  </div>
</template>

<script>
import { getTagAllRegionsDisplayValue } from '../../../../../js/util/helper'
import { EventBus } from '../../../../../js/plugins/event-bus.js'
import { mapGetters, mapActions } from 'vuex'
import AuthorBadge from './AuthorBadge.vue'
import * as loadingAnimation from '../../../../../shared/components/loading.json'
import { JOB_TYPES } from '../../../../../js/util/constants.js'

export default {
  components: {
    AuthorBadge,
  },

  props: {
    post: { default: false },
  },

  data() {
    return {
      loadingAnimationOptions: { animationData: loadingAnimation.default },
      loadingAnimate: false,
      start_date_short: null,
      end_date_short: null,
      saved: false,
      loaded: false,
      thinking: false,
      draftThinking: false,
      region_tag: null,
      jobTypes: JOB_TYPES,
    }
  },

  computed: {
    // Set all map getters
    ...mapGetters({
      settings: 'shared/getSettings',
      tenantId: 'shared/getTenantId',
      savedCloudPosts: 'careerwise/getSavedCloudposts',
    }),

    regionTagText() {
      const defaultText = this.region_tag.text
      if (this.region_tag.textOptionsByCategory) {
        return getTagAllRegionsDisplayValue(this.post.category) || defaultText
      }

      return defaultText
    },
  },

  watch: {
    savedCloudPosts: {
      handler: function (list) {
        if (list.includes(this.post.id)) {
          this.$set(this, 'saved', true)
        } else {
          this.$set(this, 'saved', false)
        }
      },

      deep: true,
    },

    post: {
      handler: function () {
        this.getRegion()
      },

      deep: true,
    },
  },

  mounted() {
    if (this.post) {
      this.getRegion()
      this.$set(this, 'loaded', true)
      if (this.savedCloudPosts.includes(this.post.id)) {
        this.$set(this, 'saved', true)
      } else {
        this.$set(this, 'saved', false)
      }
    }
  },

  methods: {
    ...mapActions({
      dispatchSaveCloudPost: 'careerwise/dispatchSaveCloudPost',
    }),

    /**
     * Save cloud post to posts
     */
    async saveCloudPost(id, tenant_id, publish = false) {
      if (tenant_id === this.tenantId || this.saved) {
        return
      }
      if (publish) {
        this.$set(this, 'thinking', true)
      } else {
        this.$set(this, 'draftThinking', true)
      }
      const payload = {
        post_id: id,
        publish,
      }

      const response = await this.dispatchSaveCloudPost(payload)
      this.$set(this, 'thinking', false)
      this.$set(this, 'draftThinking', false)
      if (response) {
        this.$set(this, 'saved', true)
      } else {
        EventBus.$emit('triggerAlertResponse', {
          status: 'error',
          message: 'Failed to save post',
        })
      }
    },

    viewPost() {
      this.$refs.viewPost.click()
    },

    goToCloudPosts() {
      this.$router.push({ name: 'Drafts' })
    },

    getRegion() {
      this.settings.tag_regions.forEach((i) => {
        if (this.post.tag_titles.includes(i.text)) {
          this.$set(this, 'region_tag', i)
        }
      })
    },
  },
}
</script>

<style scoped>
.saved-modal {
  background: rgba(255, 255, 255, 0.95);
}
</style>
